import { Grouping } from '@/components/queries/grouping'
import { GroupingConfig } from '@/components/queries/grouping-utils'
import { NodeList } from '@/components/nodes'
import type { NodeContext } from '@/components/nodes/NodeListItem'
import type { Workspace, Project, Task, Message, Document } from '@/types'
import { GROUPING_CONFIGS, NodeType } from '@/lib/config/node-grouping-configs'

// Supported node types
export type nodeType = NodeType

// Generic props for node grouping - compatible with NodeListPage
export interface NodeGroupingProps<T extends Workspace | Project | Task | Message | Document> {
  /** Array of nodes to group and display (from NodeListPage) */
  nodes?: T[]
  /** Array of tasks to group and display (from NodeListPage) */
  tasks?: T[]
  /** Array of projects to group and display (from NodeListPage) */
  projects?: T[]
  /** Array of messages to group and display (from NodeListPage) */
  messages?: T[]
  /** Current grouping option */
  groupBy: string
  /** The type of node being grouped */
  nodeType: nodeType
  /** Custom className for the container */
  className?: string
  /** Context for NodeList rendering */
  context?: NodeContext
  /** Configuration for NodeList rendering */
  config?: Record<string, unknown>
}

export function NodeGrouping<T extends Workspace | Project | Task | Message | Document>({
  nodes,
  tasks,
  projects,
  messages,
  groupBy,
  nodeType,
  className = '',
  context,
  config = {},
}: NodeGroupingProps<T>) {
  // Determine which array to use based on what's provided
  const items = nodes || tasks || projects || messages || []

  // Get the appropriate grouping configuration based on node type
  const groupingConfig = GROUPING_CONFIGS[nodeType] as GroupingConfig<T>

  // Create renderList function that uses NodeList
  const renderList = (groupItems: T[]) => (
    <NodeList items={groupItems} context={context || nodeType} config={config} />
  )

  return (
    <Grouping
      items={items}
      groupBy={groupBy}
      config={groupingConfig}
      className={className}
      renderList={renderList}
    />
  )
}
